import type { TCurrency } from '../../../types/common';
import type {
  ICellInfoProps,
  ICellInfoReturn,
  ITableBody,
} from '../../../types/data_table';
import type { TPaymentMethod } from '../../../types/table/table_row/table_cell';

import {
  convertCentsToMajorUnit,
  formatAmountWithCommas,
  getLocalizedCurrency,
} from '../../../utils/convertAmounts';
import { formatToLocalizedDateTime } from '../../../utils/dateFormats';
import { getValueFromNestedObject as getValue } from '../../../utils/getValueFromNestedObject';

/**
 * Step one of rendering a cell: pull the raw value out of the row and turn it
 * into something displayable — a string, or a `{ text, hintText, avatar }`
 * bundle, or a list (payment methods, dropdown items).
 *
 * Step two — deciding which `TableCell` variant shows that value — lives in
 * `cellProps.ts`.
 */

/** Shown wherever a row has no value for a column. */
export const EMPTY_CELL_VALUE = '-';

type TLang = NonNullable<ITableBody['lang']>;

/**
 * Glues a column's `additionalText` (a currency code, a unit, a prefix) onto
 * the value on whichever side the column asked for.
 *
 * The newlines are deliberate: they collapse to a single space in HTML, which
 * is the gap the design calls for between the two parts.
 */
const getCombinedText = (
  additionalText: string | undefined,
  placement: 'start' | 'end' | undefined,
  value: string
): string =>
  additionalText && placement
    ? `
      ${placement === 'start' ? additionalText : ''}
      ${value}
      ${placement === 'end' ? additionalText : ''}
      `
    : value;

/**
 * Resolves a column's `additionalText`, which is either a literal
 * (`directValue`) or another lookup into the row (`key`).
 */
const resolveAdditionalText = (
  row: ICellInfoProps['obj'],
  additionalText: ICellInfoProps['additionalText']
): string | undefined => {
  if (!additionalText) return undefined;

  if (additionalText.directValue) return additionalText.directValue;

  return additionalText.key ? getValue(row, additionalText.key) : undefined;
};

/**
 * Formats a numeric amount with its localized currency.
 *
 * Bails out before formatting when the value is not a number:
 * `formatAmountWithCommas` calls `.toFixed` on whatever it is given, so an API
 * that returns `'137.50'` as a string used to throw and take the whole table
 * down.
 *
 * @param inCents Whether the raw value is minor units and needs converting.
 */
const formatAmountCell = (
  value: unknown,
  currency: TCurrency | undefined,
  placement: 'start' | 'end' | undefined,
  lang: TLang,
  inCents: boolean
): string => {
  if (typeof value !== 'number') return EMPTY_CELL_VALUE;

  const amount = inCents
    ? convertCentsToMajorUnit(value, currency as string, false)
    : value;

  return getCombinedText(
    currency ? getLocalizedCurrency(lang, currency) : '',
    placement,
    formatAmountWithCommas({ amount, currency })
  );
};

/**
 * Normalizes the payment methods stored on a row. Text entries carry their
 * label in `url`, so it is copied across to `value` for the cell to render.
 */
const formatPaymentMethodCell = (
  paymentMethods: { type: 'text' | 'img'; url: string }[] | undefined
): ICellInfoReturn => {
  if (!paymentMethods?.length) return '';

  return paymentMethods.map((paymentMethod) =>
    paymentMethod.type === 'img'
      ? { ...paymentMethod }
      : { ...paymentMethod, value: paymentMethod.url }
  ) as TPaymentMethod[];
};

/**
 * Turns one row plus one column definition into the value that column shows.
 *
 * @param props Column config (`type`, `key`, `format`, ...) and the row.
 * @param lang  Drives date, currency and number localization.
 */
export const getCellInfo = (
  props: ICellInfoProps,
  lang: TLang
): ICellInfoReturn => {
  const {
    type,
    obj: row,
    additionalText,
    key,
    hintText,
    avatar,
    includeTime,
    format,
  } = props;

  /** The column's value, run through the consumer's `format` if it has one. */
  const readValue = () => {
    const value = getValue(row, key);
    return format ? format(row, value) : value;
  };

  const hint = hintText && getValue(row, hintText);
  const image = avatar && getValue(row, avatar);
  const additionalString = resolveAdditionalText(row, additionalText);

  switch (type) {
    case 'amount':
    case 'amount_cents':
      return formatAmountCell(
        readValue(),
        additionalString as TCurrency | undefined,
        additionalText?.placement,
        lang,
        type === 'amount_cents'
      );

    case 'payment_method':
      return formatPaymentMethodCell(getValue(row, key));

    case 'date': {
      const date = getValue(row, key);
      if (!date) return EMPTY_CELL_VALUE;

      const formattedDate = formatToLocalizedDateTime(date, lang, includeTime);
      // `includeTime` renders as "12 Jan 2024, 10:30" — split so the time can
      // sit under the date as a hint when the column asks for one.
      const [datePart, timePart] = formattedDate.split(', ');

      return hint ? { text: datePart, hintText: timePart } : formattedDate;
    }

    case 'dropdown':
      return readValue() || '';

    default: {
      const value = readValue();
      const combinedText = getCombinedText(
        additionalString,
        additionalText?.placement,
        value
      );

      // Both checks read the *resolved* hint and image, not the column config.
      // Testing the config meant a column that declares an `avatar` produced a
      // bundle even for rows that have no avatar and no value — an object with
      // nothing in it, which nothing downstream knew how to render.
      if (!value && !hint && !image) return EMPTY_CELL_VALUE;

      return hint || image
        ? { text: combinedText, hintText: hint, avatar: image }
        : combinedText;
    }
  }
};
